import React, { useEffect, useLayoutEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import moment from 'moment';
import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import FilledButton from '../../components/Buttons/FilledButton';
import Th from '../../components/Th';
import Pagination from '../../components/Pagination';
import useFetchingState from '../../components/Library/useFetchingState';
import DropdownMenu from '../../components/DropdownMenu';
import EmptyFolderMessage from '../../components/Library/EmptyFolderMessage';
import rowClasses from '../../components/Library/FolderRow/styles.module.scss';
import classes from '../../components/Library/styles.module.scss';
import TemplateService from '../../services/TemplateService';
import UserService from '../../services/UserService';
import { useUiStore } from '../../store';
import { useUiContext } from '../../context/UiContext';
import useConfirmDelete from '../../hooks/useConfirmDelete';
import TableRowAction from '../../components/Library/TableRowAction';
import FolderPickerModal from '../../modals/FolderPickerModal';
import { ORG_ROLES, USER_ROLES } from '../../constants/main';
import TemplatesIcon from '../../components/TemplatesIcon';

const templatesThs = [
  { field: 'title', title: 'Name' },
  { field: 'ownerName', title: 'Creator' },
  { field: 'updatedAt', title: 'Date' },
  { field: 'action', title: 'Action', noSort: true },
];

const breadcrumbsOptions = [{ label: 'Templates', to: '/templates' }];

function canDeleteTemplate(user, template) {
  if (!user || !template) return false;
  if (user.role === USER_ROLES.SUPER_ADMIN) return true;
  if (user.orgRole?.access === ORG_ROLES.OWNER) return true;
  return template.userId === user.id;
}

export default function TemplatesPage() {
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useUiContext();
  const setBreadcrumbsOptions = useUiStore(
    (state) => state.setBreadcrumbsOptions
  );

  const [selectedTemplateForUse, setSelectedTemplateForUse] = useState(null);

  const {
    page,
    setPage,
    searchInput,
    setSearchInput,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    debouncedSearchInput,
    itemsRange,
  } = useFetchingState();

  const { data: user } = useQuery('me', UserService.getMe);

  const {
    data: templates,
    refetch: refetchTemplates,
    isFetching,
  } = useQuery(
    ['templates', page, debouncedSearchInput, sortField, sortOrder],
    () =>
      TemplateService.getTemplates({
        search: debouncedSearchInput,
        limit: itemsRange,
        sortField,
        sortOrder,
        page,
      }),
    { keepPreviousData: true }
  );

  const { mutate: deleteTemplateMutation } = useMutation(
    TemplateService.deleteTemplate,
    {
      onSuccess: () => {
        notifySuccess('Template deleted successfully.');
        refetchTemplates();
      },
      onError: (error) =>
        notifyError(
          error?.response?.data?.message || 'Failed to delete template'
        ),
    }
  );

  const { mutate: cloneTemplate } = useMutation(
    ({ templateId, folderId }) =>
      TemplateService.cloneTemplate({ templateId, folderId }),
    {
      onSuccess: (data) => {
        setSelectedTemplateForUse(null);
        navigate(`/my-videos-new/${data.meeting.id}/edit?editorMode=template_using`);
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to use template'
        );
      },
    }
  );

  const handleDeleteTemplate = useConfirmDelete(deleteTemplateMutation, 'template', {
    isPermanent: true,
  });

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    if (debouncedSearchInput) {
      setPage(1);
    }
  }, [debouncedSearchInput, setPage]);

  useEffect(() => {
    setBreadcrumbsOptions(breadcrumbsOptions);
  }, [setBreadcrumbsOptions]);

  const openUseTemplateModal = (template) => setSelectedTemplateForUse(template);

  const rows = templates?.rows ?? [];
  const isLoading = isFetching;
  const isEmpty = !isLoading && !rows.length;

  return (
    <Layout
      hasUserBlock
      hasSearch
      searchInput={searchInput}
      setSearchInput={setSearchInput}
    >
      <div className={classes.Library}>
        <Navigation />
        <div className={classes.container}>
          <div className={classes.libraryHeader}>
            <div>
              <Breadcrumbs breadcrumbsOptions={breadcrumbsOptions} />
            </div>
            <div className={classes.buttonsContainer}>
              {user?.orgRole?.access !== ORG_ROLES.VIEWER && (
                <FilledButton
                  onClick={() => navigate('/templates/create')}
                  width={196}
                >
                  + Add new
                </FilledButton>
              )}
            </div>
          </div>

          <div className={classes.tableContainer}>
            <table className={classes.table}>
              <colgroup>
                <col style={{ width: '42%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '22%' }} />
              </colgroup>
              <thead>
                <tr>
                  {templatesThs.map((th) => (
                    <Th
                      key={th.field}
                      classes={classes}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      setSortField={setSortField}
                      setSortOrder={setSortOrder}
                      field={th.field}
                      searchInput={searchInput}
                      setSearchInput={setSearchInput}
                      title={th.title}
                      noSort={th.noSort}
                    />
                  ))}
                </tr>
              </thead>
              {!!rows.length && (
                <tbody>
                  {rows.map((template) => (
                    <tr key={template.id} className={rowClasses.FolderRow}>
                      <td className={rowClasses.title}>
                        <div className={rowClasses.innerContainer}>
                          <div className={rowClasses.icon}>
                            <TemplatesIcon size={26} stroke="#111111" strokeWidth={1.5} />
                          </div>
                          <div>
                            <div className={rowClasses.titleContainer}>
                              <span>{template.title}</span>
                            </div>
                            <span className={rowClasses.publishedDate}>
                              modified {template.updatedAt ? moment(template.updatedAt).format('MMM D, YYYY') : '-'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className={rowClasses.owner}>
                        {template.userId === user?.id
                          ? 'Me'
                          : template.Owner?.name}
                      </td>
                      <td className={rowClasses.publishedDate}>
                        {template.updatedAt
                          ? moment(template.updatedAt).format('MMM D, YYYY')
                          : '-'}
                      </td>
                      <td className={`${rowClasses.buttons} ${rowClasses.actions}`}>
                        <div
                          className={rowClasses.buttonsContainer}
                          style={{ width: '100%', justifyContent: 'space-between', flexDirection: 'row' }}
                        >
                          <FilledButton
                            width={190}
                            style={{ backgroundColor: 'var(--primary)', whiteSpace: 'nowrap' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openUseTemplateModal(template);
                            }}
                          >
                            Use template
                          </FilledButton>
                          <TableRowAction
                            handleEdit={() => navigate(`/templates/${template.id}/edit`)}
                            handleDelete={() => handleDeleteTemplate(template)}
                            canEdit={canDeleteTemplate(user, template)}
                            canDelete={canDeleteTemplate(user, template)}
                          />
                          <DropdownMenu
                            options={[
                              {
                                label: 'Use template',
                                onClick: () => openUseTemplateModal(template),
                              },
                              ...(canDeleteTemplate(user, template)
                                ? [
                                    {
                                      label: 'Edit',
                                      onClick: () => navigate(`/templates/${template.id}/edit`),
                                    },
                                    {
                                      label: 'Delete',
                                      onClick: () => handleDeleteTemplate(template),
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>

          {isEmpty && (
            <div className={classes.empty} role="status">
              {debouncedSearchInput
                ? "Oops! We can't find any files with that name."
                : <EmptyFolderMessage message="No templates yet" />}
            </div>
          )}

          {templates?.count > itemsRange && (
            <Pagination
              activePage={page}
              itemsCountPerPage={itemsRange}
              totalItemsCount={templates.count}
              pageRangeDisplayed={itemsRange}
              onChange={setPage}
            />
          )}
        </div>
      </div>
      <FolderPickerModal
        show={!!selectedTemplateForUse}
        onHide={() => setSelectedTemplateForUse(null)}
        onSelect={(folderId) => {
          cloneTemplate({
            templateId: selectedTemplateForUse.id,
            folderId,
          });
        }}
        title="Select destination folder"
      />
    </Layout>
  );
}
